import { forwardRef, useImperativeHandle, useRef } from "react";
import type { KarelIde } from "./karelIde";
import { useKarelIde } from "./useKarelIde";
import styles from "./KarelCanvas.module.css";

/**
 * Maps authored command tokens to `KarelIde` step methods. Both camelCase and
 * snake_case spellings are accepted so lesson data can stay canonical while
 * Python-style tokens still animate.
 */
const COMMAND_STEPS: Record<string, (ide: KarelIde) => void> = {
  move: (ide) => ide.stepMove(),
  turnLeft: (ide) => ide.stepTurnLeft(),
  turn_left: (ide) => ide.stepTurnLeft(),
  turnRight: (ide) => ide.stepTurnRight(),
  turn_right: (ide) => ide.stepTurnRight(),
  putBeeper: (ide) => ide.stepPutBeeper(),
  put_beeper: (ide) => ide.stepPutBeeper(),
  pickBeeper: (ide) => ide.stepPickBeeper(),
  pick_beeper: (ide) => ide.stepPickBeeper(),
};

export interface KarelCanvasHandle {
  getIde(): KarelIde | null;
  animateCommands(commands: string[], callback: () => void): void;
}

interface KarelCanvasProps {
  world: string;
}

export const KarelCanvas = forwardRef<KarelCanvasHandle, KarelCanvasProps>(
  function KarelCanvas({ world }, ref) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const { ideRef } = useKarelIde(world, { canvasRef, observe: canvasRef });

    useImperativeHandle(ref, () => ({
      getIde: () => ideRef.current,
      animateCommands: (commands: string[], callback: () => void) => {
        const ide = ideRef.current;
        if (!ide) {
          callback();
          return;
        }
        let index = 0;
        const step = (): void => {
          if (index >= commands.length) {
            callback();
            return;
          }
          const command = commands[index++];
          const run = COMMAND_STEPS[command];
          if (run) run(ide);
          else console.warn(`Unknown button-lesson command: ${command}`);
          setTimeout(step, 150);
        };
        step();
      },
    }));

    return <canvas ref={canvasRef} className={styles.canvas} />;
  },
);
